import { useState, useEffect } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { voteDeal, fetchMyVote } from "@/lib/api";
import { tempColor } from "@/components/public/TemperatureBadge";

export const VoteControl = ({ dealId, temperature, orientation = "horizontal", onChange }) => {
  const [temp, setTemp] = useState(temperature);
  const [myVote, setMyVote] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pulse, setPulse] = useState(false);

  useEffect(() => setTemp(temperature), [temperature]);
  useEffect(() => {
    let active = true;
    fetchMyVote(dealId).then((r) => active && setMyVote(r.my_vote)).catch(() => {});
    return () => { active = false; };
  }, [dealId]);

  const handleVote = async (value, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      const r = await voteDeal(dealId, value);
      setTemp(r.temperature);
      setMyVote(r.my_vote);
      setPulse(true);
      setTimeout(() => setPulse(false), 350);
      onChange?.(r);
    } catch (err) {
      toast.error("Abstimmung fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const color = tempColor(temp);
  const isV = orientation === "vertical";

  const hotBtn = `h-8 w-8 rounded-lg flex items-center justify-center transition-all active:scale-90 ${
    myVote === "hot" ? "bg-rose-500 text-white shadow-sm shadow-rose-500/30" : "text-slate-400 hover:bg-rose-50 hover:text-rose-500"
  }`;
  const coldBtn = `h-8 w-8 rounded-lg flex items-center justify-center transition-all active:scale-90 ${
    myVote === "cold" ? "bg-blue-500 text-white shadow-sm shadow-blue-500/30" : "text-slate-400 hover:bg-blue-50 hover:text-blue-500"
  }`;

  if (isV) {
    return (
      <div className="flex flex-row sm:flex-col items-center gap-1.5" data-testid="vote-control">
        <button type="button" onClick={(e) => handleVote("hot", e)} disabled={busy} aria-label="Heiß" data-testid="vote-hot-button" className={hotBtn}>
          <ChevronUp className="h-5 w-5" strokeWidth={2.8} />
        </button>
        <span
          className={`font-outfit font-black tracking-tight text-lg transition-transform ${pulse ? "scale-125" : "scale-100"}`}
          style={{ color }}
          data-testid="vote-temperature"
        >
          {temp}°
        </span>
        <button type="button" onClick={(e) => handleVote("cold", e)} disabled={busy} aria-label="Kalt" data-testid="vote-cold-button" className={coldBtn}>
          <ChevronDown className="h-5 w-5" strokeWidth={2.8} />
        </button>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="vote-control">
      <button type="button" onClick={(e) => handleVote("cold", e)} disabled={busy} aria-label="Kalt" data-testid="vote-cold-button" className={`h-9 w-9 rounded-full flex items-center justify-center transition-all active:scale-90 ${myVote === "cold" ? "bg-blue-500 text-white" : "text-slate-400 hover:bg-white"}`}>
        <ChevronDown className="h-5 w-5" strokeWidth={2.8} />
      </button>
      <span className={`font-outfit font-black tracking-tight px-2 min-w-[3.6rem] text-center text-xl transition-transform ${pulse ? "scale-110" : ""}`} style={{ color }} data-testid="vote-temperature">
        {temp}°
      </span>
      <button type="button" onClick={(e) => handleVote("hot", e)} disabled={busy} aria-label="Heiß" data-testid="vote-hot-button" className={`h-9 w-9 rounded-full flex items-center justify-center transition-all active:scale-90 ${myVote === "hot" ? "bg-rose-500 text-white" : "text-slate-400 hover:bg-white"}`}>
        <ChevronUp className="h-5 w-5" strokeWidth={2.8} />
      </button>
    </div>
  );
};

export default VoteControl;
